import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatures, FeatureFlags } from '../hooks/useFeatures';

type FeatureKey = keyof Omit<FeatureFlags, never>;

/**
 * Route guard that blocks access to a capability-gated page when its feature is
 * disabled for this tenant (Lushin's post-call-only default). Redirects to the
 * dashboard. While flags load, renders nothing to avoid a flash of gated content.
 *
 * `feature` accepts a LIST because a page can be reachable through more than one
 * capability. The Knowledge Base is the case that forced it: it hosts both the
 * old objection/FAQ/landmine model (`liveIntelligence`) and the live discovery
 * checklist (`livePlaybook`), and Lushin ships the second WITHOUT the first.
 * Gating on `liveIntelligence` alone made the whole page unreachable for exactly
 * the configuration it was built for.
 */
interface Props {
  /** A single capability, or several — access is granted if ANY is enabled. */
  feature: FeatureKey | FeatureKey[];
  children: React.ReactNode;
}

const FeatureProtectedRoute: React.FC<Props> = ({ feature, children }) => {
  const flags = useFeatures();
  if (flags?.loading) return null;

  const required = Array.isArray(feature) ? feature : [feature];
  const hasAccess = required.some((key) => flags?.[key]);
  if (!hasAccess) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

export default FeatureProtectedRoute;
